import { useEffect, useRef, useState } from 'react';

// material-ui
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Avatar from '@mui/material/Avatar';
import Badge from '@mui/material/Badge';
import Chip from '@mui/material/Chip';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid2';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';

// project imports
import { ThemeMode } from 'config';
import MainCard from 'ui-component/cards/MainCard';
import Transitions from 'ui-component/extended/Transitions';
import NotificationList from './NotificationList';
import useLocalization from 'hooks/useLocalization';
import { useDispatch, useSelector } from 'store';
import {
  markAllSystemNotificationsRead,
  hydrateSystemNotifications,
  type SystemNotificationItem,
} from 'store/slices/system-notifications';
import { fetchUpdates, markAllUpdatesRead, type UpdateFeedItem } from 'api/updates';

// assets
import { IconBell } from '@tabler/icons-react';

/** שורת Updates מהשרת → פריט פעמון. תיוג (mention) מקבל את הוויזואל של tag. */
function mapUpdateToNotification(u: UpdateFeedItem): SystemNotificationItem {
  return {
    id: `srv-${u.id}`,
    message: u.body ?? '',
    createdAt: Date.parse(u.createdAt) || Date.now(),
    read: u.status !== 'unread',
    visual: u.type === 'mention' ? { kind: 'tag' } : { kind: 'note' },
    fromServer: true,
    link: u.link ?? null,
  };
}

// ==============================|| NOTIFICATION ||============================== //

export default function NotificationSection() {
  const theme = useTheme();
  const downMD = useMediaQuery(theme.breakpoints.down('md'));
  const { currentLocale } = useLocalization();
  const hintLoc = currentLocale.settingsPopup.userConfig.tabs.chats.leftColumn.hintNotification;
  const dispatch = useDispatch();

  const unreadCount = useSelector(
    (state) => state.systemNotifications.items.filter((n) => !n.read).length,
  );

  const [open, setOpen] = useState(false);

  const anchorRef = useRef<any>(null);

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event: React.MouseEvent<HTMLDivElement> | MouseEvent | TouchEvent) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  const handleMarkAllRead = () => {
    //* אופטימי מקומית + התמדה בשרת (טבלת Updates) — כך המצב שורד רענון ומסונכרן בין מכשירים.
    dispatch(markAllSystemNotificationsRead());
    void markAllUpdatesRead();
  };

  //* היזון הפעמון מהשרת: בעליית האפליקציה ובכל פתיחה של הפאנל (מחליף פריטי שרת,
  //* משמר התראות שירות מקומיות מהסוקט).
  const hydrateFromServer = async () => {
    const result = await fetchUpdates({ pageSize: 50 });
    if (result) {
      dispatch(hydrateSystemNotifications(result.items.map(mapUpdateToNotification)));
    }
  };

  useEffect(() => {
    void hydrateFromServer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const prevOpen = useRef(open);
  useEffect(() => {
    if (prevOpen.current === false && open === true) {
      void hydrateFromServer();
    }
    if (prevOpen.current === true && open === false) {
      anchorRef.current.focus();
    }
    prevOpen.current = open;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <>
      <Box>
        {/* badge לא-נקראים על הפעמון עצמו — נראה גם בלי לפתוח את הפאנל,
            באותו עיצוב כמו ה-badge של גלגל השיניים (HeaderSettingsStatusIcon) */}
        <Badge
          badgeContent={unreadCount}
          color="warning"
          overlap="circular"
          invisible={unreadCount === 0}
          sx={{
            '& .MuiBadge-badge': {
              fontSize: 10,
              minWidth: 16,
              height: 16,
              fontWeight: 700,
            },
          }}
        >
          <Avatar
            variant="rounded"
            sx={{
              ...theme.typography.commonAvatar,
              ...theme.typography.mediumAvatar,
              transition: 'all .2s ease-in-out',
              bgcolor: theme.palette.mode === ThemeMode.DARK ? 'dark.main' : 'secondary.light',
              color: theme.palette.mode === ThemeMode.DARK ? 'warning.dark' : 'secondary.dark',
              '&[aria-controls="menu-list-grow"],&:hover': {
                bgcolor: theme.palette.mode === ThemeMode.DARK ? 'warning.dark' : 'secondary.dark',
                color: theme.palette.mode === ThemeMode.DARK ? 'grey.800' : 'secondary.light',
              },
            }}
            ref={anchorRef}
            aria-controls={open ? 'menu-list-grow' : undefined}
            aria-haspopup="true"
            onClick={handleToggle}
            color="inherit"
          >
            <IconBell stroke={1.5} size="20px" />
          </Avatar>
        </Badge>
      </Box>
      <Popper
        placement={downMD ? 'bottom' : 'bottom-end'}
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        modifiers={[
          {
            name: 'offset',
            options: {
              offset: [downMD ? 5 : 0, 20],
            },
          },
        ]}
      >
        {({ TransitionProps }) => (
          <ClickAwayListener onClickAway={handleClose}>
            <Transitions position={downMD ? 'top' : 'top-right'} in={open} {...TransitionProps}>
              <Paper>
                {open && (
                  <MainCard border={false} elevation={16} content={false} boxShadow shadow={theme.shadows[16]}>
                    <Grid container direction="column" spacing={2}>
                      <Grid size={12}>
                        <Grid container sx={{ alignItems: 'center', justifyContent: 'space-between', pt: 2, px: 2 }}>
                          <Grid>
                            <Stack direction="row" spacing={2} alignItems="center">
                              <Typography variant="subtitle1">{hintLoc.systemNotificationsCenterTitle}</Typography>
                              {unreadCount > 0 && (
                                <Chip
                                  size="small"
                                  label={String(unreadCount)}
                                  sx={{ color: 'background.default', bgcolor: 'warning.dark' }}
                                />
                              )}
                            </Stack>
                          </Grid>
                          <Grid>
                            {unreadCount > 0 && (
                              <Typography
                                component="button"
                                type="button"
                                variant="subtitle2"
                                color="primary"
                                onClick={handleMarkAllRead}
                                sx={{
                                  cursor: 'pointer',
                                  border: 'none',
                                  background: 'none',
                                  font: 'inherit',
                                  p: 0,
                                  textDecoration: 'underline',
                                }}
                              >
                                {hintLoc.markAllSystemRead}
                              </Typography>
                            )}
                          </Grid>
                        </Grid>
                      </Grid>
                      <Grid size={12}>
                        <Box
                          sx={{
                            height: '100%',
                            maxHeight: 'calc(100vh - 205px)',
                            overflowX: 'hidden',
                            '&::-webkit-scrollbar': { width: 5 },
                          }}
                        >
                          <Grid container direction="column" spacing={2}>
                            <Grid size={12} sx={{ p: 0 }}>
                              <Divider sx={{ my: 0 }} />
                            </Grid>
                          </Grid>
                          <NotificationList
                            emptyLabel={hintLoc.systemNotificationsEmpty}
                            onNavigated={() => setOpen(false)}
                          />
                        </Box>
                      </Grid>
                    </Grid>
                  </MainCard>
                )}
              </Paper>
            </Transitions>
          </ClickAwayListener>
        )}
      </Popper>
    </>
  );
}
